import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  #scrollFrame = 0;
  #variantFocusId = '';
  #variantPending = false;
  #variantRequest = 0;
  #variantFailed = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    document.addEventListener('scroll', this.#handlePageScroll, { capture: true, passive: true, signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.refs.variantDialog?.addEventListener('click', this.#handleOptionsClick, { signal });
    this.refs.variantDialog?.addEventListener('change', this.#handleOptionsClick, { signal });
    this.refs.variantDialog?.addEventListener('close', () => {
      this.#variantFocusId = '';
      this.refs.variantButton?.setAttribute('aria-expanded', 'false');
    }, { signal });

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    this.refs.variantDialog?.close();
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Fast scrolling can skip the buy buttons without changing their intersection
  // state (below the viewport -> above it). Check the scroll position as well.
  #handlePageScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      const buyButtons = this.#getProductForm()?.closest('.buy-buttons-block');
      const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
      if (!buyButtons || !footer) return;
      const buyRect = buyButtons.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const passedButtons = buyRect.bottom <= 0;
      const nearFooter = footerRect.top <= window.innerHeight + 200 && footerRect.bottom >= -200;
      if (passedButtons && !nearFooter && !this.#isChatActive()) {
        this.#hiddenByBottom = false;
        if (!this.#isStuck) this.#showStickyBar();
      } else if (this.#isStuck) {
        this.#hiddenByBottom = nearFooter;
        this.#hideStickyBar();
      }
    });
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#variantPending || this.#variantFailed || this.refs.addToCartButton.disabled) return;
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;
    this.closeVariantSelector();
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const picker = event.target.closest('variant-picker');
    if (picker && picker !== this.#getVariantPicker()) return;
    const request = ++this.#variantRequest;
    this.#variantPending = true;
    this.#variantFailed = false;
    this.refs.addToCartButton.disabled = true;
    this.refs.stickyBar.setAttribute('aria-busy', 'true');
    this.#renderVariantOptions();

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (request !== this.#variantRequest) return;
        if (!detail?.html) throw new Error('Variant response is missing product HTML');

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) throw new Error('Variant response is missing sticky bar');

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) throw new Error('Variant response is missing sticky bar content');

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Other product listeners update the form on the same promise.
        requestAnimationFrame(() => {
          if (request !== this.#variantRequest || this.#abortController.signal.aborted) return;
          this.#variantPending = false;
          this.refs.stickyBar.removeAttribute('aria-busy');
          this.refs.variantButton?.setAttribute('aria-expanded', String(Boolean(this.refs.variantDialog?.open)));
          this.#updateButtonText();
          this.#renderVariantOptions();
        });
      })
      .catch((error) => {
        if (request !== this.#variantRequest) return;
        this.#variantPending = false;
        // Never add the old variant after a failed selection request. A new
        // selection through the original picker can safely recover the state.
        this.#variantFailed = true;
        this.refs.stickyBar.removeAttribute('aria-busy');
        this.refs.addToCartButton.disabled = true;
        this.#renderVariantOptions();
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    const label = variantTitleElement.querySelector('.sticky-add-to-cart__variant-label') ?? variantTitleElement;
    label.textContent = selectedOptions;
  };

  openVariantSelector = () => {
    const dialog = this.refs.variantDialog;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    this.#renderVariantOptions();
    if (!this.refs.variantOptions?.childElementCount) return;
    dialog.showModal();
    this.refs.variantButton?.setAttribute('aria-expanded', 'true');
  };

  closeVariantSelector = () => {
    this.refs.variantDialog?.close();
    this.refs.variantButton?.setAttribute('aria-expanded', 'false');
  };

  #getVariantPicker() {
    return this.closest('.shopify-section')?.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );
  }

  #handleOptionsClick = (event) => {
    const dialog = this.refs.variantDialog;
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        this.closeVariantSelector();
      }
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('[data-sticky-option-id]') : null;
    if (!button || this.#variantPending) return;
    const picker = this.#getVariantPicker();
    const source = Array.from(picker?.querySelectorAll('[data-option-value-id]') ?? [])
      .find((option) => option.dataset.optionValueId === button.dataset.stickyOptionId);
    // Proxy the main picker instead of maintaining a second variant/cart state.
    if (source instanceof HTMLInputElement) {
      if (source.checked && !this.#variantFailed) return;
      source.checked = true;
      source.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (source instanceof HTMLOptionElement && source.parentElement instanceof HTMLSelectElement) {
      const select = source.parentElement;
      if (source.selected && !this.#variantFailed) return;
      select.value = source.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #renderVariantOptions() {
    const { variantOptions: container, variantAddToCartButton: addButton, variantStatus: status } = this.refs;
    const picker = this.#getVariantPicker();
    if (!container || !picker) return;
    const focusId = document.activeElement?.dataset?.stickyOptionId || this.#variantFocusId;
    this.#variantFocusId = focusId || '';
    // Keep the original swatch styles and inherited variant tokens, including
    // merchant custom colours, instead of maintaining a separate visual system.
    container.classList.add('variant-picker');
    for (const name of Array.from(container.classList)) {
      if (name.startsWith('variant-picker--custom-')) container.classList.remove(name);
    }
    for (const name of picker.classList) {
      if (name.startsWith('variant-picker--custom-')) container.classList.add(name);
    }
    const pickerStyle = getComputedStyle(picker);
    for (const name of pickerStyle) {
      if (/^--(variant|color-variant|color-selected-variant|focus-outline|style-border-swatch)/.test(name)) {
        container.style.setProperty(name, pickerStyle.getPropertyValue(name));
      }
    }
    const groups = document.createDocumentFragment();
    for (const group of picker.querySelectorAll('.variant-option')) {
      const sources = Array.from(group.querySelectorAll('input[type="radio"], select option'));
      if (!sources.length) {
        const single = document.createElement('p');
        single.textContent = group.textContent.trim();
        groups.append(single);
        continue;
      }
      const hasSwatches = sources.every((source) => source instanceof HTMLInputElement &&
        source.closest('label')?.querySelector('.swatch'));
      if (hasSwatches) {
        const fieldset = document.createElement('fieldset');
        fieldset.className = `${group.className} sticky-add-to-cart__swatches`;
        const sourceLegend = group.querySelector('legend');
        if (sourceLegend) fieldset.append(sourceLegend.cloneNode(true));
        const values = document.createElement('div');
        values.className = 'sticky-add-to-cart__swatch-values';
        for (const source of sources) {
          // Clone only the native label/swatch, never another variant-picker.
          // Unique radio names keep the visual controls separate from the main form.
          const label = source.closest('label').cloneNode(true);
          label.dataset.stickyOptionId = source.dataset.optionValueId;
          label.classList.add('sticky-add-to-cart__swatch');
          label.removeAttribute('for');
          for (const node of [label, ...label.querySelectorAll('*')]) {
            node.removeAttribute('id');
            node.removeAttribute('ref');
          }
          const radio = label.querySelector('input');
          radio.name = `sticky-${this.dataset.productId}-${source.dataset.fieldsetIndex}`;
          radio.removeAttribute('form');
          radio.removeAttribute('data-option-value-id');
          radio.dataset.stickyOptionId = source.dataset.optionValueId;
          radio.checked = source.checked;
          radio.toggleAttribute('checked', source.checked);
          radio.disabled = this.#variantPending || source.disabled;
          values.append(label);
        }
        fieldset.append(values);
        groups.append(fieldset);
        continue;
      }
      if (sources.length === 1) {
        const single = document.createElement('p');
        const source = sources[0];
        const label = source instanceof HTMLOptionElement ? source.textContent.trim() :
          source.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent.trim() || source.value;
        single.textContent = `${source.dataset.optionName}: ${label}`;
        groups.append(single);
        continue;
      }
      const fieldset = document.createElement('fieldset');
      const legend = document.createElement('legend');
      legend.textContent = sources[0].dataset.optionName ||
        Array.from(group.querySelector('legend, label')?.childNodes ?? [])
          .filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('').trim();
      const values = document.createElement('div');
      values.className = 'sticky-add-to-cart__option-values';
      for (const source of sources) {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'sticky-add-to-cart__option';
        option.dataset.stickyOptionId = source.dataset.optionValueId;
        const selected = source instanceof HTMLInputElement ? source.checked : source.selected;
        option.setAttribute('aria-pressed', String(selected));
        const unavailable = source.dataset.optionAvailable === 'false' || source.getAttribute('aria-disabled') === 'true';
        option.classList.toggle('is-unavailable', unavailable);
        option.textContent = source instanceof HTMLOptionElement ? source.textContent.trim() :
          source.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent.trim() ||
          source.getAttribute('aria-label') || source.value;
        option.disabled = this.#variantPending || source.disabled;
        values.append(option);
      }
      fieldset.append(legend, values);
      groups.append(fieldset);
    }
    container.replaceChildren(groups);
    if (focusId && !this.#variantPending && this.refs.variantDialog?.open) {
      Array.from(container.querySelectorAll('input[data-sticky-option-id], button[data-sticky-option-id]'))
        .find((option) => option.dataset.stickyOptionId === focusId)?.focus({ preventScroll: true });
    }
    if (addButton) {
      addButton.disabled = this.#variantPending || this.#variantFailed || this.refs.addToCartButton.disabled;
      const label = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
      addButton.textContent = label?.textContent.trim() || this.refs.addToCartButton.getAttribute('aria-label');
      if (!addButton.disabled && this.#currentQuantity > 1) addButton.textContent += ` (${this.#currentQuantity})`;
    }
    if (status) {
      status.textContent = this.#variantFailed ? this.dataset.variantError :
        this.#variantPending ? '…' : '';
    }
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
